import React, { useState, Fragment } from 'react';
import { useUserStore } from '@/stores/userStore';
import useInputState from '@/hooks/useInputState';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  SwipeablePanels,
  SwipeablePanel,
} from '@/components/ui/SwipeablePanels';
import Backdrop from '@/components/ui/feedback/Backdrop';
import { useTheme } from '@/theme/config';
import { showToastFromSnackbar } from '@/utils/showToast';
import { useAdminSignin, useAdminMe } from '@/hooks/api/useAdmin';
import { useLocales } from '@/locales';
import { v4 as uuidv4 } from 'uuid';
import { Eye, EyeOff, Mail } from 'lucide-react';

export default function AdminAuth() {
  const { user, setUser } = useUserStore(state => ({
    user: state.user,
    setUser: state.setUser,
  }));
  const { translate } = useLocales();
  const [validEmail, setValidEmail] = useState(true);
  const [validPassword, setValidPassword] = useState(true);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [email, setEmail, resetEmail] = useInputState('');
  const [altEmail, setAltEmail] = useInputState('');
  const [password, setPassword, resetPassword] = useInputState('');
  const navigate = useNavigate();

  const socketToken = uuidv4();

  // React Query mutations and queries
  const { mutate: signin, isPending: signingIn } = useAdminSignin();

  // Check if user token is valid on mount
  const { isLoading: validatingToken } = useAdminMe({
    enabled: !!user.token,
    retry: false,
    onSuccess: response => {
      if (response.status === 200) {
        const userData = {
          id: response.data.users._id,
          role: response.data.users.role,
          isVerified: true,
          details: response.data.users,
          token: user.token,
          socketUUID: socketToken,
        };
        setUser(userData);
        navigate('/dashboard/all-applications');
      }
    },
    onError: () => {
      // Token invalid, stay on login page
    },
  });

  const authenticating = signingIn || validatingToken;

  const handleSubmit = e => {
    e.preventDefault();
    if (!email || !password) {
      setValidEmail(false);
      setValidPassword(false);
      return showToastFromSnackbar(translate('message_7'), {
        variant: 'warning',
      });
    }

    signin(
      { email, password },
      {
        onSuccess: response => {
          if (response.status === 200 && response.data.status === 'success') {
            resetEmail();
            resetPassword();
            const userData = {
              id: response.data.data._id,
              role: response.data.data.role,
              isVerified: true,
              details: response.data.data,
              token: response.data.token,
              socketUUID: socketToken,
            };
            setUser(userData);
            showToastFromSnackbar(translate('Login_Successful'), {
              variant: 'success',
            });
            setTimeout(() => {
              if (response.data.data.role === 'operator') {
                return navigate('/callCenter/allClients');
              } else {
                return navigate('/dashboard/all-applications');
              }
            }, 1000);
          }
        },
        onError: () => {
          setValidEmail(false);
          setValidPassword(false);
          showToastFromSnackbar(
            translate('Check_email_address_password_again'),
            { variant: 'error' },
          );
        },
      },
    );
  };

  const theme = useTheme();
  const [value, setValue] = useState(0);

  const handleChangeIndex = index => {
    setValue(index);
  };

  return (
    <Fragment>
      <Backdrop open={authenticating} handleCloseBackDrop={() => {}} />
      <div className="auth auth-admin ">
        <div className="auth__sidebar">
          <div className="form-container">
            <div
              role="button"
              tabIndex={0}
              className="form-container__logo"
              onClick={() => navigate('/')}
              onKeyDown={e => e.key === 'Enter' && navigate('/')}
            >
              <div className="form-container__logo--sample" />
            </div>
            <SwipeablePanels
              axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
              index={value}
              onChangeIndex={handleChangeIndex}
            >
              <SwipeablePanel value={value} index={0} dir={theme.direction}>
                <form
                  className="form-container__body margin-side-2"
                  onSubmit={handleSubmit}
                >
                  <h3>{translate('entrance')}</h3>
                  <div className={`form-container__input`}>
                    <h4 className="text-disabled">{translate('Your_login')}</h4>
                    <div
                      className={`form-container__input--field ${
                        !validEmail && 'error'
                      }`}
                    >
                      <Input
                        type={'email'}
                        placeholder={translate('Enter_login')}
                        name={'login'}
                        className={'text-field-input'}
                        value={email}
                        onChange={e => {
                          setValidEmail(true);
                          setEmail(e);
                        }}
                      />
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                  <div className="form-container__input">
                    <h4 className="text-disabled">{translate('Password')}</h4>
                    <div
                      className={`form-container__input--field ${
                        !validPassword && 'error'
                      }`}
                    >
                      <Input
                        type={passwordVisible ? 'text' : 'password'}
                        placeholder={translate('Enter_your_password')}
                        name={'password'}
                        className={'text-field-input'}
                        value={password}
                        onChange={e => {
                          setValidPassword(true);
                          setPassword(e);
                        }}
                      />
                      {passwordVisible ? (
                        <EyeOff
                          className="h-5 w-5 text-gray-400 cursor-pointer"
                          onClick={() => setPasswordVisible(false)}
                        />
                      ) : (
                        <Eye
                          className="h-5 w-5 text-gray-400 cursor-pointer"
                          onClick={() => setPasswordVisible(true)}
                        />
                      )}
                    </div>
                  </div>
                  <div className="auth__type">
                    <Button
                      className="form-container__button padding-height"
                      type="submit"
                    >
                      {translate('Enter')}
                    </Button>
                    <button
                      type="button"
                      className="subtitle text-center text-blue margin-top-1 bg-transparent border-none cursor-pointer w-full"
                      onClick={() => handleChangeIndex(1)}
                    >
                      {translate('Forgot_password')}
                    </button>
                  </div>
                </form>
              </SwipeablePanel>
              <SwipeablePanel value={value} index={1} dir={theme.direction}>
                <form
                  className="form-container__body margin-side-2"
                  onSubmit={handleSubmit}
                >
                  <h3>{translate('Password_recovery')}</h3>
                  <div className={`form-container__input`}>
                    <h4 className="text-disabled">{translate('Email')}</h4>
                    <div className={`form-container__input--field`}>
                      <Input
                        type={'email'}
                        placeholder={'Ввудите логин...'}
                        name={'resetEmail'}
                        className={'text-field-input'}
                        value={altEmail}
                        onChange={setAltEmail}
                      />
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                  <div className="auth__type">
                    <Button
                      className="form-container__button padding-height"
                      type="submit"
                    >
                      {translate('Reset_Password')}
                    </Button>
                    <button
                      type="button"
                      className="subtitle text-center text-blue margin-top-1 bg-transparent border-none cursor-pointer w-full"
                      onClick={() => handleChangeIndex(0)}
                    >
                      {translate('Go_Back')}
                    </button>
                  </div>
                </form>
              </SwipeablePanel>
            </SwipeablePanels>
          </div>
        </div>
      </div>
    </Fragment>
  );
}
